// aiToolWrappersExtended — Extensions till aiToolRegistry.ts — exponerar
// AI-intelligence-helpers (BONUS-1/BONUS-4) som callable tools.
//
// Inte ersätter AI_TOOLS — extenderar. Kombineras via mergeToolRegistries().

import type { ToolDefinition } from '@/lib/aiToolRegistry';

export type ExtendedToolName =
  | 'semantic_search'
  | 'lint_workflow'
  | 'classify_usecase'
  | 'detect_patterns'
  | 'explain_workflow'
  | 'rank_connectors'
  | 'decompose_microservices'
  | 'generate_test_payload'
  | 'profile_performance'
  | 'generate_pr_description'
  | 'describe_block_connections'
  | 'list_recent_mutations'
  // I-005: Process-motor tool-use (Enterprise-only)
  | 'trigger_workflow_run'
  | 'list_recent_workflow_runs'
  // Diagram-authoring (D4) — bygger multi-sheet-diagram via staging.
  | 'plan_diagram'
  | 'create_sheet'
  | 'batch_create_blocks'
  | 'batch_create_connectors'
  | 'create_group'
  | 'create_cross_sheet_link'
  | 'validate_diagram';

export const EXTENDED_TOOLS: ToolDefinition[] = [
  {
    name: 'semantic_search',
    description: 'Semantic search över user workflows. Returnerar top-N matches.',
    parameters: [
      { name: 'query', type: 'string', required: true, description: 'Natural-language query.' },
      { name: 'limit', type: 'number', required: false, description: 'Max results, default 5.' },
    ],
    sideEffect: 'read',
  },
  {
    name: 'lint_workflow',
    description: 'Kör best-practice-linter på nuvarande workflow. Returnerar issues.',
    parameters: [],
    sideEffect: 'read',
  },
  {
    name: 'classify_usecase',
    description: 'Klassificera vilket use-case workflowet matchar (IT/BP/decision/org).',
    parameters: [],
    sideEffect: 'read',
  },
  {
    name: 'detect_patterns',
    description: 'Hitta kända patterns (state-machine, fan-out, pipeline) i workflow.',
    parameters: [],
    sideEffect: 'read',
  },
  {
    name: 'explain_workflow',
    description: 'Generera natural-language-beskrivning av workflowet.',
    parameters: [
      { name: 'level', type: 'string', required: false, description: 'brief/medium/detailed', enum: ['brief', 'medium', 'detailed'] },
      { name: 'lang', type: 'string', required: false, description: 'sv/en', enum: ['sv', 'en'] },
    ],
    sideEffect: 'read',
  },
  {
    name: 'rank_connectors',
    description: 'Ranka kandidat-connectors (REST/GraphQL/Postgres/etc) efter suitability.',
    parameters: [
      { name: 'use_case', type: 'string', required: true, description: 'Beskrivning av use-case.' },
    ],
    sideEffect: 'read',
  },
  {
    name: 'decompose_microservices',
    description: 'Identifiera subflows (BFS-clustering) som kan splittas till separate workflows.',
    parameters: [
      { name: 'min_cluster_size', type: 'number', required: false, description: 'Min block per cluster, default 3.' },
    ],
    sideEffect: 'read',
  },
  {
    name: 'generate_test_payload',
    description: 'Generera test-payload per block med 4 varianter (happy/error/edge/stress).',
    parameters: [
      { name: 'block_id', type: 'string', required: true, description: 'Target block-id.' },
      { name: 'variant', type: 'string', required: false, description: 'Specifik variant eller "all".' },
    ],
    sideEffect: 'read',
  },
  {
    name: 'profile_performance',
    description: 'Profile historical runs → bottlenecks + recommendations.',
    parameters: [
      { name: 'run_history_ids', type: 'array', required: true, description: 'List av run-snapshot-id:n att aggregera.' },
    ],
    sideEffect: 'read',
    minTier: 'pro',
  },
  {
    name: 'generate_pr_description',
    description: 'Generera GitHub-style PR-description (What/Why/Testing/Risks) för staged AI-changes.',
    parameters: [
      { name: 'session_id', type: 'string', required: true, description: 'Preview-session-id.' },
      { name: 'ai_reasoning', type: 'string', required: false, description: 'Optional high-level motivering.' },
    ],
    sideEffect: 'read',
    minTier: 'advanced',
  },
  {
    name: 'describe_block_connections',
    description: 'Natural-language-sammanfattning av ett blocks connector-graf (utgående + inkommande kanter). Låter LLM resonera om relation utan hela WorkflowState i prompten.',
    parameters: [
      { name: 'block_id', type: 'string', required: true, description: 'Block-id att beskriva.' },
      { name: 'lang', type: 'string', required: false, description: 'sv/en', enum: ['sv', 'en'] },
    ],
    sideEffect: 'read',
  },
  {
    name: 'list_recent_mutations',
    description: 'Returnerar user:s EGNA senaste workflow-mutations (insert/update/delete) inom tidsfönstret. Används för AI-debug: "vad ändrade jag senast?". RLS-skyddad — ser bara egna mutations.',
    parameters: [
      { name: 'minutes', type: 'number', required: false, description: 'Tidsfönster i minuter (1-1440). Default 30.' },
      { name: 'limit', type: 'number', required: false, description: 'Max rader (1-200). Default 50.' },
    ],
    sideEffect: 'read',
  },
  // ── I-005 Process-motor tool-use (Enterprise-only) ─────────────
  // AI kan trigga workflow-runs eller läsa run-historik. Dessa tools
  // gate:as till Enterprise-tier både client-side (via minTier) och
  // server-side (via workflow_runs-insert-trigger från P-001).
  {
    name: 'trigger_workflow_run',
    description: 'Triggar en run av en workflow som process. Kräver Enterprise-plan. AI ska INTE kalla denna utan explicit user-godkännande — caller ska visa approval-dialog först.',
    parameters: [
      { name: 'workflow_id', type: 'string', required: true, description: 'UUID för workflow att köra.' },
      { name: 'trigger_data', type: 'object', required: false, description: 'Valfri payload som skickas som trigger-context.' },
      { name: 'trigger_kind', type: 'string', required: false, description: 'manual | test', enum: ['manual', 'test'] },
    ],
    sideEffect: 'write',
    minTier: 'enterprise',
  },
  {
    name: 'list_recent_workflow_runs',
    description: 'Läser user:s senaste workflow-runs (status + trigger-kind + timing). Enterprise-only.',
    parameters: [
      { name: 'workflow_id', type: 'string', required: false, description: 'Filtrera på specifik workflow. Utan filter: alla user:s runs.' },
      { name: 'limit', type: 'number', required: false, description: 'Max rader (1-200). Default 50.' },
    ],
    sideEffect: 'read',
    minTier: 'enterprise',
  },
  // ── Diagram-authoring (D4) ─────────────────────────────────────
  // Pure implementation i src/lib/aiDiagramTools.ts; writes sker via
  // aiStagingSession, inte direkt mot live-state.
  {
    name: 'plan_diagram',
    description: 'Föreslå sheet-uppdelning + style för ett multi-sheet-diagram innan faktisk authoring. Returnerar DiagramPlan (sheet-namn, färger, estimated block-count). Ingen mutation.',
    parameters: [
      { name: 'goal', type: 'string', required: true, description: 'Natural-language beskrivning av vad diagrammet ska visa.' },
      { name: 'sheet_count', type: 'number', required: false, description: 'Önskat antal sheets (1-10). Default baseras på style.' },
      { name: 'style', type: 'string', required: false, description: 'architecture | module-graph | pipeline | layered | swimlane | hub-spoke', enum: ['architecture', 'module-graph', 'pipeline', 'layered', 'swimlane', 'hub-spoke'] },
    ],
    sideEffect: 'read',
  },
  {
    name: 'create_sheet',
    description: 'Skapa en ny WorkflowSheet i staging-session. Returnerar sheet-id. Kallas en gång per sheet i ett multi-sheet-diagram.',
    parameters: [
      { name: 'name', type: 'string', required: true, description: 'Sheet-namn (t.ex. "① App Shell").' },
      { name: 'description', type: 'string', required: false, description: 'Meta-beskrivning som visas i sheet-tab-tooltip.' },
      { name: 'color', type: 'string', required: false, description: 'Sheet-accent-färg (hex).' },
      { name: 'icon', type: 'string', required: false, description: 'Lucide-icon-namn för sheet-tabben.' },
    ],
    sideEffect: 'write',
    minTier: 'advanced',
  },
  {
    name: 'batch_create_blocks',
    description: 'Skapa N blocks på en sheet i ett enda anrop. Grid-snappar koordinater, tilldelar UUID:er, returnerar idMap {ref→uuid} så efterföljande connector-calls kan refera blocks via client-refs. Föredras framför upprepade add_block-calls.',
    parameters: [
      { name: 'sheet_id', type: 'string', required: true, description: 'Target sheet-id (från create_sheet).' },
      { name: 'blocks', type: 'array', required: true, description: 'Array av { ref, type, x, y, config:{name,description?,icon?}, color? }.' },
    ],
    sideEffect: 'write',
    minTier: 'advanced',
  },
  {
    name: 'batch_create_connectors',
    description: 'Skapa N connectors på en sheet. Input använder client-refs (från batch_create_blocks.idMap) eller faktiska block-ids. De-dupe exact-duplicates, validerar port-enum, defaultar right/left.',
    parameters: [
      { name: 'sheet_id', type: 'string', required: true, description: 'Target sheet-id.' },
      { name: 'edges', type: 'array', required: true, description: 'Array av { fromRef, toRef, fromPort?, toPort?, label? }.' },
    ],
    sideEffect: 'write',
    minTier: 'advanced',
  },
  {
    name: 'create_group',
    description: 'Skapa en group eller layer på en sheet. Layer (collapsed=true) kräver collapsedX/Y. Block kan bara vara medlem i en group — samma blockId i flera groups flaggas av validate_diagram.',
    parameters: [
      { name: 'sheet_id', type: 'string', required: true, description: 'Target sheet-id.' },
      { name: 'block_ids', type: 'array', required: true, description: 'Member blocks (refs eller ids).' },
      { name: 'color', type: 'string', required: true, description: 'Group-färg (hex).' },
      { name: 'name', type: 'string', required: false, description: 'Group-namn.' },
      { name: 'collapsed', type: 'boolean', required: false, description: 'true → navigable layer.' },
      { name: 'collapsed_x', type: 'number', required: false, description: 'Layer-kortets X-position (grid-snappas).' },
      { name: 'collapsed_y', type: 'number', required: false, description: 'Layer-kortets Y-position (grid-snappas).' },
    ],
    sideEffect: 'write',
    minTier: 'advanced',
  },
  {
    name: 'create_cross_sheet_link',
    description: 'Skapa en cross-sheet-connector mellan grannsheets. Strikt adjacency-regel: |fromSheetIndex - toSheetIndex| === 1. Icke-grannsheets returnerar error=non-adjacent.',
    parameters: [
      { name: 'from_sheet_index', type: 'number', required: true, description: 'Källsheet (0-indexed).' },
      { name: 'to_sheet_index', type: 'number', required: true, description: 'Målsheet.' },
      { name: 'from_block_id', type: 'string', required: true, description: 'Källblock-id.' },
      { name: 'to_block_id', type: 'string', required: true, description: 'Målblock-id.' },
      { name: 'from_port', type: 'string', required: false, description: 'Källport (8-enum).' },
      { name: 'to_port', type: 'string', required: false, description: 'Målport.' },
      { name: 'label', type: 'string', required: false, description: 'Valfri label på linjen.' },
    ],
    sideEffect: 'write',
    minTier: 'advanced',
  },
  {
    name: 'validate_diagram',
    description: 'Kör alla invariant-checks på staging-state. Returnerar ValidateDiagramReport med violations (off-grid, invalid-port, duplicate-group-membership, non-adjacent-cross-sheet, dangling-connector, layer-missing-coords, duplicate-id). AI:n BÖR kalla denna innan approveAiStage.',
    parameters: [],
    sideEffect: 'read',
  },
];

export function mergeToolRegistries(
  base: ToolDefinition[],
  extended: ToolDefinition[] = EXTENDED_TOOLS,
): ToolDefinition[] {
  const byName = new Map<string, ToolDefinition>();
  for (const t of base) byName.set(String(t.name), t);
  for (const t of extended) byName.set(String(t.name), t);
  return Array.from(byName.values());
}

export function extendedToolByName(name: string): ToolDefinition | null {
  return EXTENDED_TOOLS.find(t => String(t.name) === name) ?? null;
}

export function readOnlyTools(tools: ToolDefinition[] = EXTENDED_TOOLS): ToolDefinition[] {
  return tools.filter(t => t.sideEffect === 'read');
}
